if (!customElements.get('custom-results-trustpilot-cards')) {
  customElements.define(
    'custom-results-trustpilot-cards',
    class CustomResultsTrustpilotCards extends HTMLElement {
      async connectedCallback() {
        this.slider = this.querySelector('[data-custom-results-trustpilot-slider]');
        this.originalCount = Number(this.slider?.getAttribute('data-original-count')) || 0;
        this.syncCardHeights = this.syncCardHeights.bind(this);

        this.initTrustpilotSummary();
        this.syncCardHeights();
        await this.initSlider();
        this.syncCardHeights();
        this.bindHeightSync();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        window.removeEventListener('resize', this.syncCardHeights);
        this.swiper?.destroy?.();
      }

      bindHeightSync() {
        window.addEventListener('resize', this.syncCardHeights);

        this.querySelectorAll('.custom-results-trustpilot-card__image').forEach((image) => {
          if (!image.complete) {
            image.addEventListener('load', this.syncCardHeights, { once: true });
          }
        });
      }

      syncCardHeights() {
        if (!this.slider) {
          return;
        }

        const track = this.slider.querySelector('.custom-results-trustpilot-cards__track');
        const slides = [
          ...this.slider.querySelectorAll('.custom-results-trustpilot-cards__slide'),
        ].slice(0, this.originalCount);
        const cards = slides
          .map((slide) => slide.querySelector('.custom-results-trustpilot-card'))
          .filter(Boolean);

        if (!cards.length) {
          return;
        }

        this.style.removeProperty('--custom-results-trustpilot-cards-card-min-height');
        cards.forEach((card) => {
          card.style.minHeight = '';
        });

        if (track) {
          track.style.alignItems = 'flex-start';
        }

        const maxHeight = cards.reduce(
          (tallest, card) => Math.max(tallest, card.getBoundingClientRect().height),
          0
        );

        if (track) {
          track.style.alignItems = '';
        }

        if (maxHeight <= 0) {
          return;
        }

        this.style.setProperty(
          '--custom-results-trustpilot-cards-card-min-height',
          `${Math.ceil(maxHeight)}px`
        );
      }

      initTrustpilotSummary() {
        this.onSectionLoad = this.onSectionLoad.bind(this);
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        this.loadTrustpilotSummary();
      }

      onSectionLoad(event) {
        if (event.target?.contains(this)) {
          this.loadTrustpilotSummary();
        }
      }

      async loadTrustpilotSummary() {
        const businessUnitId = this.dataset.trustpilotBusinessUnitId;
        const templateId = this.dataset.trustpilotTemplateId;
        const token = this.dataset.trustpilotToken;
        const locale = this.dataset.trustpilotLocale || 'en-US';

        if (!businessUnitId || !templateId || !token) {
          return;
        }

        const params = new URLSearchParams({
          businessUnitId,
          locale,
          theme: 'light',
          token,
        });

        try {
          const response = await fetch(
            `https://widget.trustpilot.com/trustbox-data/${templateId}?${params.toString()}`
          );

          if (!response.ok) {
            return;
          }

          const data = await response.json();
          this.updateTrustpilotSummary(data);
        } catch {
          return;
        }
      }

      updateTrustpilotSummary(data) {
        const scoreElement = this.querySelector('[data-trustpilot-score]');
        const reviewCountElement = this.querySelector('[data-trustpilot-review-count]');
        const ratingLinkElement = this.querySelector('[data-trustpilot-rating-link]');
        const reviewsLinkElement = this.querySelector('[data-trustpilot-reviews-link]');
        const totalReviews = data?.businessEntity?.numberOfReviews?.total;
        const scoreLabel = data?.starsString;
        const syncScore = this.dataset.trustpilotSyncScore === 'true';

        if (scoreElement && scoreLabel && syncScore) {
          scoreElement.textContent = scoreLabel;
        }

        if (reviewCountElement && totalReviews != null) {
          const locale = this.dataset.trustpilotLocale || 'en-US';
          reviewCountElement.textContent = new Intl.NumberFormat(locale).format(totalReviews);
        }

        if (ratingLinkElement && scoreElement) {
          ratingLinkElement.setAttribute(
            'aria-label',
            `Trustpilot rating: ${scoreElement.textContent}`
          );
        }

        if (reviewsLinkElement && reviewCountElement) {
          const reviewsMeta = reviewCountElement.closest('.custom-results-trustpilot-cards__trustpilot-meta');
          const reviewsLabel = reviewsMeta?.textContent?.trim() || reviewCountElement.textContent;

          reviewsLinkElement.setAttribute(
            'aria-label',
            `Read ${reviewsLabel} on Trustpilot`
          );
        }
      }

      async initSlider() {
        this.slider = this.querySelector('[data-custom-results-trustpilot-slider]');

        if (!this.slider || this.slider.swiper) {
          return;
        }

        this.previousButton = this.querySelector('[data-custom-results-trustpilot-prev]');
        this.nextButton = this.querySelector('[data-custom-results-trustpilot-next]');
        this.pagination = this.querySelector('[data-custom-results-trustpilot-dots]');
        this.originalCount = Number(this.slider.getAttribute('data-original-count')) || 0;

        if (this.originalCount === 0) {
          return;
        }

        await this.waitForSwiper();

        this.swiper = new Swiper(this.slider, {
          slidesPerView: 'auto',
          spaceBetween: 15,
          loop: true,
          centeredSlides: true,
          allowTouchMove: true,
          grabCursor: true,
          speed: 500,
          navigation: {
            nextEl: this.nextButton,
            prevEl: this.previousButton,
          },
          pagination: {
            el: this.pagination,
            type: 'bullets',
            clickable: true,
            bulletClass: 'custom-results-trustpilot-cards__dot',
            bulletActiveClass: 'is-active',
            renderBullet: (index, className) => {
              if (this.originalCount > 0 && index >= this.originalCount) {
                return `<button class="${className}" type="button" tabindex="-1" aria-hidden="true" style="display:none"></button>`;
              }

              return `<button class="${className}" type="button" aria-label="Show transformation ${index + 1}"></button>`;
            },
          },
          breakpoints: {
            768: {
              spaceBetween: 41,
            },
          },
          on: {
            init: () => {
              this.syncCardHeights();
            },
            slideChange: (swiper) => {
              if (!this.originalCount) {
                return;
              }

              const realIndex = swiper.realIndex % this.originalCount;
              const bullets = this.pagination
                ? this.pagination.querySelectorAll('.custom-results-trustpilot-cards__dot')
                : [];

              bullets.forEach((bullet, index) => {
                bullet.classList.toggle('is-active', index === realIndex);
              });
            },
          },
        });
      }

      waitForSwiper() {
        return new Promise((resolve) => {
          if (window.Swiper) {
            resolve();
            return;
          }

          const checkSwiper = () => {
            if (window.Swiper) {
              resolve();
              return;
            }

            window.setTimeout(checkSwiper, 50);
          };

          checkSwiper();
        });
      }
    }
  );
}
